import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, isSaturday, isValid } from 'date-fns';

interface CreateSheetDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateSheet: (date: Date, prefillPreviousWeek: boolean) => Promise<void>;
  existingDates: Date[];
  handleCancel: () => void;
}

export const CreateSheetDialog: React.FC<CreateSheetDialogProps> = ({
  open,
  onClose,
  onCreateSheet,
  existingDates,
  handleCancel,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [prefillPreviousWeek, setPrefillPreviousWeek] = useState(false);

  const handleCreate = async () => {
    if (selectedDate) {
      await onCreateSheet(selectedDate, prefillPreviousWeek);
      onClose();
    }
  };

  const shouldDisableDate = useCallback((date: Date) => {
    // Only allow Saturdays that don't already exist
    if (!isSaturday(date)) return true;

    // Check if this date already exists
    return existingDates.some(existingDate =>
      existingDate.getFullYear() === date.getFullYear() &&
      existingDate.getMonth() === date.getMonth() &&
      existingDate.getDate() === date.getDate()
    );
  }, [existingDates]);

  // Find the next available Saturday
  const findNextAvailableSaturday = useCallback((startDate: Date): Date => {
    let date = startDate;
    while (shouldDisableDate(date)) {
      date = addDays(date, 1);
    }
    return date;
  }, [shouldDisableDate]);

  // Set initial date to next available Saturday when dialog opens
  useEffect(() => {
    if (open && !selectedDate) {
      const nextSaturday = findNextAvailableSaturday(new Date());
      setSelectedDate(nextSaturday);
    }
  }, [open, findNextAvailableSaturday, selectedDate]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      closeAfterTransition
    >
      <DialogTitle>
        Create New Sheet
        <IconButton
          aria-label="close"
          onClick={() => {
            handleCancel();
            onClose();
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <DatePicker
            label="Week Ending"
            value={selectedDate}
            onChange={(newDate) => {
              if (newDate && isValid(newDate)) {
                setSelectedDate(newDate);
              }
            }}
            shouldDisableDate={shouldDisableDate}
            sx={{ width: '100%', mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={prefillPreviousWeek}
                onChange={(e) => setPrefillPreviousWeek(e.target.checked)}
              />
            }
            label="Pre-fill with previous data"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!selectedDate}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 